import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AuthorPublisherUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { PublisherCreate } from "../../../publisher/components/create/PublisherCreate";
import { PublisherCreateStore } from "../../../publisher/store";
import { InputNumber } from "../../../common/controls";
import { InputIntegerChoices } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type AuthorPublisherUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  publisherSelectedValue?: { displayId: string; id: string } | null;
  onPublisherChange?: (value: string) => void;
  onPublisherSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  publisherOptions?: { displayId: string; id: string }[];
  publisherError?: string;
  percentage: number;
  percentageSelectedValue?: { displayId: string; id: string } | null;
  onPercentageChange?: (value: number) => void;
  percentageError?: string;
  durationInYears: number | undefined;
  durationInYearsSelectedValue?: { displayId: string; id: string } | null;
  onDurationInYearsChange?: (value: number | undefined) => void;
  durationInYearsError?: string;
  autoRenewalYears: number | undefined;
  autoRenewalYearsSelectedValue?: { displayId: string; id: string } | null;
  onAutoRenewalYearsChange?: (value: number | undefined) => void;
  autoRenewalYearsError?: string;
  authorPublisherUpdateStore: AuthorPublisherUpdateStore;
};

export const AuthorPublisherUpdateForm: React.FC<AuthorPublisherUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onPublisherSelected = () => {},
      publisherOptions = [],
      publisherSelectedValue = null,
      publisherError,
      onPublisherChange = () => {},
      percentage = 0,
      percentageError,
      onPercentageChange = () => {},
      durationInYears = undefined,
      durationInYearsError,
      onDurationInYearsChange = () => {},
      autoRenewalYears = undefined,
      autoRenewalYearsError,
      onAutoRenewalYearsChange = () => {},
      authorPublisherUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AuthorPublisher"
              mode="update"
              values={{}}
              options={{ publisher: publisherOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "publisher":
                    onPublisherSelected?.(valor);
                    break;
                  case "percentage":
                    onPercentageChange?.(valor);
                    break;
                  case "durationInYears":
                    onDurationInYearsChange?.(valor);
                    break;
                  case "autoRenewalYears":
                    onAutoRenewalYearsChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={authorPublisherUpdateStore.getCardErrors("null")}
              title={langStore.groupAuthorPublisherLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="publisher"
                  label={langStore.AuthorPublisherPublisherLabel}
                  autoFocus
                  error={Boolean(publisherError)}
                  helperText={
                    publisherError ||
                    authorPublisherUpdateStore.warnings["publisher"]?.message
                  }
                  selectedValue={publisherSelectedValue}
                  onChange={(value: string) => onPublisherChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onPublisherSelected(value)
                  }
                  options={publisherOptions}
                  description={langStore.AuthorPublisherPublisherDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "publisher_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new PublisherCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <PublisherCreate
                          publisherCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewPublisher"
                      cancelMethod="cancelNewPublisher"
                    />
                  )}
                />
                <InputNumber
                  value={percentage}
                  error={Boolean(percentageError)}
                  warning={
                    !percentageError &&
                    Boolean(authorPublisherUpdateStore.warnings["percentage"])
                  }
                  helperText={
                    percentageError ||
                    authorPublisherUpdateStore.warnings["percentage"]?.message
                  }
                  label={langStore.AuthorPublisherPercentageLabel}
                  decimalScale={2}
                  description={langStore.AuthorPublisherPercentageDescription}
                  onChange={(value: number) => onPercentageChange(value)}
                />
                <InputIntegerChoices
                  value={durationInYears}
                  nullable={true}
                  min={1}
                  max={100}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  error={Boolean(durationInYearsError)}
                  helperText={
                    durationInYearsError ||
                    authorPublisherUpdateStore.warnings["durationInYears"]
                      ?.message
                  }
                  label={langStore.AuthorPublisherDurationInYearsLabel}
                  nullText={langStore.AuthorPublisherDurationInYearsNullText}
                  description={
                    langStore.AuthorPublisherDurationInYearsDescription
                  }
                  onChange={(value: number | undefined) =>
                    onDurationInYearsChange(value)
                  }
                />
                <InputIntegerChoices
                  value={autoRenewalYears}
                  nullable={true}
                  min={1}
                  max={50}
                  unitSingular={"año"}
                  unitPlural={"años"}
                  error={Boolean(autoRenewalYearsError)}
                  helperText={
                    autoRenewalYearsError ||
                    authorPublisherUpdateStore.warnings["autoRenewalYears"]
                      ?.message
                  }
                  label={langStore.AuthorPublisherAutoRenewalYearsLabel}
                  nullText={langStore.AuthorPublisherAutoRenewalYearsNullText}
                  description={
                    langStore.AuthorPublisherAutoRenewalYearsDescription
                  }
                  onChange={(value: number | undefined) =>
                    onAutoRenewalYearsChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
